import {
  APP_ID,
  APP_INITIALIZER,
  ApplicationConfig,
  ErrorHandler,
  LOCALE_ID,
  PLATFORM_ID,
  inject,
  provideZonelessChangeDetection,
} from '@angular/core';
import {
  IsActiveMatchOptions,
  Router,
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
  withPreloading,
  withRouterConfig,
  withViewTransitions,
} from '@angular/router';

import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import '@angular/common/locales/global/en';
import '@angular/common/locales/global/ro';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions, withI18nSupport } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideServiceWorker } from '@angular/service-worker';
import { IconDefinition } from '@ant-design/icons-angular';
import {
  CheckCircleFill,
  CloseOutline,
  DeleteOutline,
  EditOutline,
  FileTextOutline,
  LoadingOutline,
  MailOutline,
  MenuOutline,
  PlusOutline,
  PrinterOutline,
  SaveOutline,
  SearchOutline,
  SettingOutline,
  ShoppingCartOutline,
  UserOutline,
} from '@ant-design/icons-angular/icons';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateService,
  provideMissingTranslationHandler,
  provideTranslateService,
} from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular';
import { NZ_I18N, en_US } from 'ng-zorro-antd/i18n';
import { provideNzIcons } from 'ng-zorro-antd/icon';
import { environment } from 'src/environments/environment';
import { BUILD_TIMESTAMP } from '../environments/build-timestamp';
import { appInitializerFactory } from './app.component';
import { routes } from './app.routes';
import { GlobalErrorHandler } from './classes';
import { ENGLISH_US, SupportedLocales } from './constants';
import { PagesEnum } from './enums';
import { AuthGuard } from './guards';
import { ro_RO_patched } from './i18n/ro-patch';
import {
  apiKeyInterceptor,
  duplicateRequestInterceptor,
  errorNotificationInterceptor,
  jwtInterceptor,
  refreshTokenInterceptor,
} from './interceptors';
import {
  AppUpdateModelResponseDto,
  CartItemModelResponseDto,
  ChatMessageModelResponseDto,
  NotificationModelResponseDto,
  OrderModelResponseDto,
  PaymentModelResponseDto,
  SessionModelResponseDto,
  SuborderModelResponseDto,
  VenueModelResponseDto,
  WaiterCommandModelResponseDto,
} from './models';
import {
  APP_UPDATES_WEBSOCKET_SERVICE,
  ApiService,
  AuthenticationService,
  CART_ITEMS_WEBSOCKET_SERVICE,
  IntegrationsOrchestratorService,
  LocalStorageService,
  NOTIFICATIONS_WEBSOCKET_SERVICE,
  ORDERS_WEBSOCKET_SERVICE,
  PAYMENTS_WEBSOCKET_SERVICE,
  PrinterDiscoveryService,
  SESSIONS_WEBSOCKET_SERVICE,
  SUBORDERS_WEBSOCKET_SERVICE,
  SUPPORT_CHAT_WEBSOCKET_SERVICE,
  TitleService,
  UserAccountService,
  VENUE_WEBSOCKET_SERVICE,
  WAITER_COMMANDS_VENUES_WEBSOCKET_SERVICE,
  WAITER_COMMANDS_WEBSOCKET_SERVICE,
  WebsocketService,
} from './services';
import { ApiHealthService } from './services/api-health.service';
import { LocalesService } from './services/locales.service';
import { WaytrIconsService } from './shared';
import { FlagBasedPreloadingStrategy } from './shared/strategy';
import { LANDING_PAGE_URL } from './tokens';

// Only load essential icons initially to reduce bundle size
const criticalIcons: IconDefinition[] = [
  CheckCircleFill,
  CloseOutline,
  DeleteOutline,
  EditOutline,
  FileTextOutline,
  LoadingOutline,
  MailOutline,
  MenuOutline,
  PlusOutline,
  PrinterOutline,
  SaveOutline,
  SearchOutline,
  SettingOutline,
  ShoppingCartOutline,
  UserOutline,
];

export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    if (!environment.production) {
      console.warn(`Missing translation for key: ${params.key}`);
    }
    return params.key;
  }
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideAnimations(),
    provideRouter(
      routes,
      withRouterConfig({
        paramsInheritanceStrategy: 'always',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' }),
      withPreloading(FlagBasedPreloadingStrategy),
      withComponentInputBinding(),
      // Enable View Transitions API for smooth page transitions
      // Progressive enhancement: falls back gracefully in unsupported browsers
      withViewTransitions({
        onViewTransitionCreated: ({ transition }) => {
          const router = inject(Router);
          const targetUrl = router.getCurrentNavigation()?.finalUrl;
          const currentUrl = router.url;

          // Skip transitions for pages with backdrop-filter effects (login, register)
          // View transitions create isolated rendering contexts that break backdrop-filter
          const skipPages = [
            `/${PagesEnum.LOGIN}`,
            `/${PagesEnum.REGISTER}`,
            `/${PagesEnum.FORGOT_PASSWORD}`,
            `/${PagesEnum.RESET_PASSWORD}`,
          ];
          const isNavigatingToSkipPage = skipPages.some((page) => targetUrl?.toString().includes(page));
          const isNavigatingFromSkipPage = skipPages.some((page) => currentUrl.includes(page));

          if (isNavigatingToSkipPage || isNavigatingFromSkipPage) {
            transition.skipTransition();
            return;
          }

          // Skip transitions for fragment/query param only changes (e.g., anchor links)
          const config: IsActiveMatchOptions = {
            paths: 'exact',
            matrixParams: 'exact',
            fragment: 'ignored',
            queryParams: 'ignored',
          };

          if (targetUrl && router.isActive(targetUrl, config)) {
            transition.skipTransition();
          }
        },
      }),
    ),
    provideHttpClient(
      withInterceptors([
        duplicateRequestInterceptor,
        apiKeyInterceptor,
        refreshTokenInterceptor,
        jwtInterceptor,
        errorNotificationInterceptor,
      ]),
      withFetch(),
    ),
    provideZonelessChangeDetection(),
    provideClientHydration(
      withEventReplay(),
      withI18nSupport(),
      withHttpTransferCacheOptions({
        includeHeaders: ['authorization', 'api-key'],
        includeRequestsWithAuthHeaders: true,
        filter: (req) => {
          // Cache only GET requests and exclude real-time endpoints
          return req.method === 'GET' && !req.url.includes('/realtime');
        },
      }),
    ),
    provideTranslateService({
      fallbackLang: ENGLISH_US,
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n/',
        suffix: `.json?v=${BUILD_TIMESTAMP}`,
      }),
      missingTranslationHandler: provideMissingTranslationHandler(MyMissingTranslationHandler),
    }),
    provideNzIcons(criticalIcons),
    { provide: APP_ID, useValue: 'waytr-pos' },
    { provide: LANDING_PAGE_URL, useValue: environment.LANDING_PAGE_URL },
    {
      provide: NZ_I18N,
      useFactory: () => {
        const localId = inject(LOCALE_ID);
        switch (localId) {
          case SupportedLocales.EN_US:
            return en_US;
          /** keep the same with angular.json/i18n/locales configuration **/
          case SupportedLocales.RO_RO:
            return ro_RO_patched;
          default:
            return en_US;
        }
      },
    },
    ApiService,
    PrinterDiscoveryService,
    IntegrationsOrchestratorService,
    AuthGuard,
    {
      provide: ErrorHandler,
      useClass: GlobalErrorHandler,
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializerFactory,
      deps: [TitleService, LocalesService, LocalStorageService, TranslateService, WaytrIconsService, ApiHealthService, PLATFORM_ID],
      multi: true,
    },
    // Create multiple instances of WebsocketService for each domain with JWT authentication
    // Note: We use lazy dependency injection here to avoid circular dependencies with VenueService
    {
      provide: APP_UPDATES_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<AppUpdateModelResponseDto>('app-updates', userAccountService, authService, router);
      },
    },
    {
      provide: CART_ITEMS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<CartItemModelResponseDto>('cart-items', userAccountService, authService, router);
      },
    },
    {
      provide: ORDERS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<OrderModelResponseDto>('orders', userAccountService, authService, router);
      },
    },
    {
      provide: SUBORDERS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<SuborderModelResponseDto>('suborders', userAccountService, authService, router);
      },
    },
    {
      provide: SESSIONS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<SessionModelResponseDto>('sessions', userAccountService, authService, router);
      },
    },
    {
      provide: NOTIFICATIONS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<NotificationModelResponseDto>('notifications', userAccountService, authService, router);
      },
    },
    {
      provide: WAITER_COMMANDS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<WaiterCommandModelResponseDto>('waiter_commands', userAccountService, authService, router);
      },
    },
    {
      provide: WAITER_COMMANDS_VENUES_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<WaiterCommandModelResponseDto>('waiter_commands_venues', userAccountService, authService, router);
      },
    },
    {
      provide: VENUE_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<VenueModelResponseDto>('venue', userAccountService, authService, router);
      },
    },
    {
      provide: SUPPORT_CHAT_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<ChatMessageModelResponseDto>('support-chat', userAccountService, authService, router);
      },
    },
    {
      provide: PAYMENTS_WEBSOCKET_SERVICE,
      useFactory: () => {
        const userAccountService = inject(UserAccountService);
        const authService = inject(AuthenticationService);
        const router = inject(Router);
        return new WebsocketService<PaymentModelResponseDto>('payments', userAccountService, authService, router);
      },
    },
    provideServiceWorker('ngsw-worker.js', {
      enabled: true,
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
};
